import { CHECKOUT_BREAK_POINT } from 'shared/consts';
import styled, { css } from 'styled-components';
import { DIMENSIONS } from '@hqo/react-components-library/dist/utils';
import { ROW_BASIS } from 'components/resources-list-item/consts';

export const skeletonAnimation = () => css`
  background: ${({ theme }) => theme?.colors?.greys?.sharedGreyBackground};
  background: ${({ theme }) =>
    `linear-gradient(110deg, ${theme?.colors?.greys?.adminGreyMedium} 8%, ${theme?.colors?.greys?.adminGreyLightAlt} 18%, ${theme?.colors?.greys?.adminGreyMedium} 33%)`};
  border-radius: 5px;
  background-size: 200% 100%;
  animation: 1.5s shine linear infinite;

  @keyframes shine {
    to {
      background-position-x: -200%;
    }
  }
`;

export const Container = styled.div<{ isSwipeModalContent?: boolean }>`
  padding: ${({ isSwipeModalContent }) => (isSwipeModalContent ? '0 16px 28px 16px' : '28px 16px')};
  max-width: 100vw;

  .desktop {
    display: none;
  }

  @media (min-width: ${CHECKOUT_BREAK_POINT}px) {
    .desktop {
      display: flex;
    }

    .mobile {
      display: none;
    }
  }
`;

export const Square = styled.div`
  width: 30px;
  height: 28px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme?.colors?.greys?.sharedGreyBackground ?? '#EDEDED'};
  ${skeletonAnimation};
`;

export const MaViewBlock = styled.div`
  width: 100%;
  height: 270px;
  background-color: ${({ theme }) => theme?.colors?.greys?.sharedGreyBackground ?? '#EDEDED'};
  ${skeletonAnimation};
`;

export const Bar = styled.div`
  width: 100%;
  flex-grow: 1;
  height: 28px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme?.colors?.greys?.sharedGreyBackground ?? '#EDEDED'};
  ${skeletonAnimation};
`;

export const DesktopBar = styled.div`
  width: 286px;
  height: 28px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme?.colors?.greys?.sharedGreyBackground ?? '#EDEDED'};
  ${skeletonAnimation};
`;

export const CounterBar = styled.div`
  margin-top: 25px;
  width: 98px;
  height: 15px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme?.colors?.greys?.sharedGreyBackground ?? '#EDEDED'};
  ${skeletonAnimation};

  @media (min-width: ${CHECKOUT_BREAK_POINT}px) {
    margin-top: 0;
  }
`;

export const TopCounterBar = styled.div`
  width: 98px;
  height: 15px;
  margin-bottom: 10px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme?.colors?.greys?.sharedGreyBackground ?? '#EDEDED'};
  ${skeletonAnimation};

  @media (min-width: ${CHECKOUT_BREAK_POINT}px) {
    margin-bottom: 0;
  }
`;

export const ResourceTile = styled.div<{ isDesktop?: boolean }>`
  width: 120px;
  height: 78px;
  ${({ isDesktop }) => isDesktop && 'width: 197px; height: 128px;'}
  border-radius: 4px;
  background-color: ${({ theme }) => theme?.colors?.greys?.sharedGreyBackground ?? '#EDEDED'};
  ${skeletonAnimation};
`;

export const ResourceTitle = styled.div<{ isDesktop?: boolean }>`
  width: 132px;
  height: 15px;
  ${({ isDesktop }) => isDesktop && 'width: 185px; height: 24px;'}
  border-radius: 4px;
  background-color: ${({ theme }) => theme?.colors?.greys?.sharedGreyBackground ?? '#EDEDED'};
  ${skeletonAnimation};
`;

export const ResourceSubtitle = styled.div<{ isDesktop?: boolean }>`
  width: 98px;
  height: 15px;
  ${({ isDesktop }) => isDesktop && 'width: 161px; height: 24px;'}
  border-radius: 4px;
  background-color: ${({ theme }) => theme?.colors?.greys?.sharedGreyBackground ?? '#EDEDED'};
  ${skeletonAnimation};
`;

export const FlexContainer = styled.div<{
  isColumnDirection?: boolean;
  gap: number;
  isWidth?: boolean;
  isWrap?: boolean;
  flex?: number;
}>`
  display: flex;
  ${({ isWidth }) => isWidth && 'width: 100%'};
  flex-direction: ${({ isColumnDirection }) => (isColumnDirection ? 'column' : 'row')};
  gap: ${({ gap }) => gap}px;
  ${({ isWrap }) => isWrap && 'flex-wrap: wrap'};
  ${({ flex }) => flex && `flex: ${flex}`};
  position: relative;
`;

/*
 * The redesigned page's loading state: the List/Floorplan toggle, the controls row and a stack of
 * row cards, in the shapes and sizes they actually load into — see `ResourceListRow` and the
 * controls in `FilterBar`. The tiles above mirror the photo-tile list instead, which is what the
 * page still shows for a building with no floorplan.
 */
export const RowsSkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  /* The page drops its own padding while loading, so the skeleton carries it — same values the tile
     skeleton uses, so the two loading states start in the same place. */
  padding: 28px 16px;
  box-sizing: border-box;
  max-width: 100vw;
`;

export const SkeletonBlock = styled.div<{ width?: string; height: string; radius?: string; grow?: boolean }>`
  width: ${({ width }) => width ?? '100%'};
  ${({ grow }) => grow && 'flex: 1;'}
  height: ${({ height }) => height};
  flex-shrink: 0;
  ${skeletonAnimation};
  border-radius: ${({ radius }) => radius ?? '4px'};
`;

export const SkeletonRowCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.palette.systemGreyMedium};
  border-radius: 14px;
  /* Wraps where the real cards wrap — several per line on a desktop, one on a phone. */
  flex: 0 1 ${ROW_BASIS};
  min-width: 0;
  max-width: 100%;

  @media only screen and (max-width: ${DIMENSIONS.MEDIUM}) {
    flex: none;
    width: 100%;
  }
`;

/* The cards, laid out as the list lays them out. */
export const SkeletonRowGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 10px;
  width: 100%;
`;

/*
 * The controls above them: on a desktop the location field and the date field sit side by side, each
 * capped like the real ones; on a phone they stack. Mirrored rather than approximated, so nothing
 * jumps sideways when the controls arrive.
 */
export const SkeletonControlsRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  margin: 20px 0;

  @media only screen and (max-width: ${DIMENSIONS.MEDIUM}) {
    flex-direction: column;
    gap: 14px;
  }
`;

export const SkeletonLocationField = styled.div`
  width: 413px;
  max-width: 50%;

  @media only screen and (max-width: ${DIMENSIONS.MEDIUM}) {
    width: 100%;
    max-width: none;
  }
`;

export const SkeletonDateField = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  max-width: 423px;

  @media only screen and (max-width: ${DIMENSIONS.MEDIUM}) {
    max-width: none;
  }
`;
